import { useEffect, useRef } from 'react';
import { identity } from 'ramda';

import { HIGHLIGHT_CSS_CLASS } from 'js/app/components/main/Games/constants';

// hook to detect clicks outside of a element
export const useClickOutSide = (ref, handlerFn, isTouch = true) => {
    const hasClickOutsideRef = useRef(true);

    const setHasClickOutside = (isClickOutside) => {
        hasClickOutsideRef.current = isClickOutside;
    };

    useEffect(() => {
        if (!ref) {
            return;
        }

        const listener = event => {
            if (!ref.current || ref.current.contains(event.target)) {
                setHasClickOutside(false);
                return;
            }

            if (hasClickOutsideRef.current) {
                return;
            }

            handlerFn(event);
            setHasClickOutside(true);
        };

        document.addEventListener('mousedown', listener);

        if (isTouch) {
            document.addEventListener('touchstart', listener);
        }

        return () => {
            document.removeEventListener('mousedown', listener);

            if (isTouch) {
                document.removeEventListener('touchstart', listener);
            }
        };
    }, [ref, handlerFn, isTouch]);
};

export const decodeHTMLEntities = (text = '') => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;

    return textArea.value;
};

export const fromPath = (object, pathProp) => {
    if (!pathProp) {
        return object;
    }

    let path = pathProp;

    if (typeof path === 'string') {
        path = path.split('.');
    }

    return path.reduce((value, key) =>
        value == null
            ? value
            : value[key],
    object
    );
};

let support;

/**
 * Check browser webp support
 * @returns {boolean}
 */
export const isWebpSupported = () => {
    if (typeof support !== 'undefined') {
        return support;
    }

    const elem = typeof document === 'object'
        ? document.createElement('canvas')
        : {};

    support = elem.toDataURL('image/webp')
        .indexOf('data:image/webp') === 0;

    return support;
};

export const fromArrayToObject = ({
    data = [],
    key = 'id',
    filter,
    parse = identity
}) => {
    return data.reduce((acc, item) => {
        const parseData = parse(item);

        if (filter && !filter(parseData)) {
            return acc;
        }

        return {
            ...acc,
            [parseData[key]]: parseData
        };
    }, {});
};

export const highlightText = (text) => {
    const regexp = /\*[^*]*\*/gm;
    const matches = text.match(regexp);

    return matches?.length
        ? matches.reduce((string, match) =>
            string.replace(match, `<span class="${HIGHLIGHT_CSS_CLASS}">${match.slice(1, -1)}</span>`)
        , text)
        : text;
};

export const inRange = (num, start, end) => {
    let min = start;
    let max = end;

    if (end < start) {
        min = end;
        max = start;
    }

    return num >= min && num <= max;
};

export const changeToCamelCase = (key) => key.replace(/([-_]\w)/g, (str) => str[1].toUpperCase());

export const parseHTMLAttributes = (attribs = {}) => Object.entries(attribs)
    .reduce((acc, [key, value]) => ({
        ...acc,
        [changeToCamelCase(key)]: value === 'true'
            ? true
            : value === 'false'
                ? false
                : value
    }), attribs);

export const reverseDate = (birthDate, mark = '-') => birthDate.split('-').reverse()
    .join(mark);

export const getErrorMessage = (error, translate) => {
    const errorData = error?.data || error?.response?.data || {};

    const errorMessage =
        errorData.errorText ||
        errorData.errorMessage ||
        errorData.playerMessage;

    return translate(`error.${errorData.errorCode}`, errorMessage);
};

export const parseStrInArray = (str) => str
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const lowerFirstLetter = string => string.charAt(0).toLowerCase() + string.slice(1);
